import React from 'react';
import { DataTable } from '@/components/ui/data-table';
import { Transaction } from '@/types/transaction';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDate, formatCurrency } from '@/utils/formatters';
import { ExternalLink, CheckCircle, Info } from 'lucide-react';
import { hasProperty } from '@/utils/typeHelpers';
import { useModal } from '@/contexts/ModalContext';

interface TransactionDataTableRowProps {
  transaction: Transaction;
  showId?: boolean;
  onClick?: () => void;
}

const TransactionDataTableRow: React.FC<TransactionDataTableRowProps> = ({ 
  transaction,
  showId = false,
  onClick
}) => {
  const { openModal } = useModal();
  
  // Default stripe URL for viewing transactions
  const stripeTransactionUrl = transaction.accountId ? 
    `https://dashboard.stripe.com/${transaction.accountId}/payments/${transaction.payment_intent_id || transaction.id}` : 
    `https://dashboard.stripe.com/payments/${transaction.payment_intent_id || transaction.id}`;
  
  // Normalize the KeepMore transaction flag with improved logic
  const isKeepMoreTransaction = 
    (transaction.is_keepmore_transaction === true || transaction.isKeepMoreTransaction === true) &&
    !(transaction.is_keepmore_transaction === false || transaction.isKeepMoreTransaction === false);
  
  // Process timestamp correctly
  const timestamp = transaction.created_at 
    ? new Date(isNaN(Number(transaction.created_at)) ? transaction.created_at : Number(transaction.created_at) * 1000)
    : new Date(transaction.created * 1000);
  
  // Handle payment method display
  const displayPaymentMethod = () => {
    if (transaction.paymentMethod) {
      return transaction.paymentMethod.replace('_', ' ');
    }
    if (Array.isArray(transaction.payment_method_types) && transaction.payment_method_types.length > 0) {
      return transaction.payment_method_types[0].replace('_', ' ');
    }
    
    return 'Unknown';
  };
  
  // Get customer display name safely
  const getCustomerDisplayName = () => {
    // Check if customer is an object
    if (typeof transaction.customer === 'object' && transaction.customer !== null) {
      if (hasProperty(transaction.customer, 'name')) {
        return String(transaction.customer.name);
      } 
      if (hasProperty(transaction.customer, 'email')) {
        return String(transaction.customer.email);
      }
      if (hasProperty(transaction.customer, 'id')) {
        return String(transaction.customer.id);
      }
    }
    
    // If it's a string, use it directly
    if (typeof transaction.customer === 'string') {
      return transaction.customer;
    }

    // Default fallback
    return "Unknown Customer";
  };
  

  // Updated row click handler to open the modal
  const handleRowClick = () => {
    // Open the transaction modal with this transaction's ID
    openModal('transaction', transaction.id);
    
    // If there's a custom onClick handler provided, also call that
    if (onClick) {
      onClick();
    }
  };

  return (
    <DataTable.Row 
      entityType="transaction" 
      entityId={transaction.id}
      onClick={handleRowClick}
    >
      <DataTable.Cell className='text-xs font-semibold'>{formatDate(timestamp)}</DataTable.Cell>
      
      <DataTable.Cell>
        <div className="flex items-center gap-1">
          {transaction.customerEmail ? transaction.customerEmail : getCustomerDisplayName()}
        </div>
      </DataTable.Cell>
      
      <DataTable.Cell>
        {(() => {
          let grossAmount = 0;
          if (transaction.gross_amount_cents !== null && transaction.gross_amount_cents !== undefined) {
            grossAmount = transaction.gross_amount_cents / 100;
          } else if (transaction.amount !== null && transaction.amount !== undefined) {
            if (transaction.amount > 100) {
              grossAmount = transaction.amount / 100;
            } else {
              grossAmount = transaction.amount;
            }
          }
          return formatCurrency(grossAmount);
        })()}
      </DataTable.Cell>
      
      <DataTable.Cell>
        {(() => {

          let grossAmount = 0;
          if (transaction.gross_amount_cents !== null && transaction.gross_amount_cents !== undefined) {
            grossAmount = transaction.gross_amount_cents / 100;
          } else if (transaction.amount !== null && transaction.amount !== undefined) {  
            if (transaction.amount > 100) {
              grossAmount = transaction.amount / 100;
            } else {
              grossAmount = transaction.amount;
            }
          }
          const stripeFee = (transaction.stripe_fee_cents !== null && transaction.stripe_fee_cents !== undefined)
            ? transaction.stripe_fee_cents / 100 
            : 0;
          const appFee = (transaction.application_fee_cents !== null && transaction.application_fee_cents !== undefined)
            ? transaction.application_fee_cents / 100 
            : 0;
          
          const netAmountCents = transaction.net_amount_cents ?? transaction.net_received_cents ?? null;
          if (netAmountCents !== null && netAmountCents !== undefined) {
            const netAmount = netAmountCents / 100;
            if (netAmount < grossAmount) {
              return formatCurrency(netAmount);
            }
          }
          
          // Calculate net amount from gross - fees
          // If stripe_fee is 0, estimate it (2.9% + $0.30 for cards)
          let finalStripeFee = stripeFee;
          if (finalStripeFee === 0 && grossAmount > 0) {
            finalStripeFee = (grossAmount * 0.029) + 0.30;
          }
          
          return formatCurrency(grossAmount - finalStripeFee - appFee);
        })()}
      </DataTable.Cell>
      
      <DataTable.Cell>
        <Badge variant="outline" className="capitalize">
          {displayPaymentMethod()}
        </Badge>
      </DataTable.Cell>
      
      <DataTable.Cell>
        <Badge 
          variant={
            transaction.status === 'succeeded' ? 'success' : 
            transaction.status === 'processing' ? 'warning' : 'destructive'
          }
          className="capitalize"
        >
          {transaction.status.replace('_', ' ')}
        </Badge>
      </DataTable.Cell>
        
      <DataTable.Actions className="sticky right-0 z-10">
        <Button 
          variant="ghost" 
          size="sm"
          asChild
        >
          <a href={stripeTransactionUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5">
            <ExternalLink className="h-3 w-3" />
            <span className="text-xs">DirectStripe</span>
          </a>
        </Button>
      </DataTable.Actions>
    </DataTable.Row>
  );
};

export default TransactionDataTableRow;